import React, { useEffect, useState, useRef } from 'react'
import { collection, query, where, orderBy, onSnapshot, addDoc, updateDoc, doc, getDocs } from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { db, storage } from '../../firebase.config'
import { useAuth } from '../../context/AuthContext'
import AppLayout from '../../components/layout/AppLayout'

export default function SafetyManualsPage() {
  const { orgId, isAdmin } = useAuth()
  const [manuals, setManuals] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Seed demo docs on first load (only if empty)
  useEffect(() => {
    if (!orgId) return
    ;(async () => {
      const snap = await getDocs(query(collection(db, 'organizations', orgId, 'safetyManuals')))
      if (snap.empty) {
        const seedDocs = [
          { title: 'OSHA Fall Protection in Construction', fileUrl: '', fileType: 'pdf', orgId, acknowledgments: [], createdAt: new Date(Date.now() - 7 * 86400000).toISOString(), updatedAt: new Date().toISOString() },
          { title: 'OSHA Scaffolding Safety Guide', fileUrl: '', fileType: 'pdf', orgId, acknowledgments: [], createdAt: new Date(Date.now() - 3 * 86400000).toISOString(), updatedAt: new Date().toISOString() },
        ]
        for (const d of seedDocs) await addDoc(collection(db, 'organizations', orgId, 'safetyManuals'), d)
      }
    })()
  }, [orgId])

  useEffect(() => {
    if (!orgId) return
    const unsub = onSnapshot(
      query(collection(db, 'organizations', orgId, 'safetyManuals'), orderBy('createdAt', 'desc')),
      snap => { setManuals(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false) }
    )
    return () => unsub()
  }, [orgId])

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !orgId) return

    const title = prompt('Manual title:', file.name.replace(/\.[^/.]+$/, ''))
    if (!title) {
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    setUploading(true)
    try {
      // Upload to Firebase Storage
      const storageRef = ref(storage, `organizations/${orgId}/safetyManuals/${Date.now()}_${file.name}`)
      await uploadBytes(storageRef, file)
      const fileUrl = await getDownloadURL(storageRef)

      // Save doc to Firestore
      await addDoc(collection(db, 'organizations', orgId, 'safetyManuals'), {
        orgId,
        title,
        fileUrl,
        fileType: file.type || 'application/pdf',
        fileName: file.name,
        acknowledgments: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })
    } catch (err: any) {
      alert('Upload failed: ' + (err.message || 'Unknown error'))
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleAcknowledge = async (manualId: string) => {
    if (!orgId) return
    const manual = manuals.find(m => m.id === manualId)
    if (!manual) return
    const current = manual.acknowledgments || []
    // In a real app you'd use auth context for the current user's name
    const name = 'Current User' // placeholder — should come from auth
    if (current.includes(name)) return
    await updateDoc(doc(db, 'organizations', orgId, 'safetyManuals', manualId), {
      acknowledgments: [...current, name],
      updatedAt: new Date().toISOString(),
    })
  }

  const formatDate = (iso: string) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''

  return (
    <AppLayout title="Safety Manuals">
      <div className="mb-6 flex items-center justify-between">
        <input type="text" placeholder="Search manuals..." className="border border-gray-300 rounded-lg px-4 py-2 text-sm w-64" />
        {isAdmin() && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx"
              className="hidden"
              onChange={handleFileSelect}
              id="manual-upload"
            />
            <label htmlFor="manual-upload">
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:bg-blue-300"
              >
                {uploading ? 'Uploading...' : '+ Upload Manual'}
              </button>
            </label>
          </>
        )}
      </div>
      <div className="grid grid-cols-3 gap-6">
        {manuals.map(m => {
          const ackCount = m.acknowledgments?.length || 0
          return (
            <div key={m.id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <div className="flex items-start gap-3 mb-4">
                <span className="text-3xl">📄</span>
                <div>
                  <h3 className="font-semibold text-gray-900">{m.title}</h3>
                  <p className="text-xs text-gray-400 mt-1">Uploaded {formatDate(m.createdAt)}</p>
                  {m.fileName && <p className="text-xs text-gray-400">{m.fileName}</p>}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">{ackCount} acknowledgments</span>
                <div className="flex gap-3">
                  {m.fileUrl && (
                    <a href={m.fileUrl} target="_blank" rel="noreferrer" className="text-blue-600 text-sm hover:underline">Open</a>
                  )}
                  {!m.fileUrl && <span className="text-xs text-gray-400">No file</span>}
                </div>
              </div>
            </div>
          )
        })}
        {manuals.length === 0 && (
          <p className="col-span-3 text-center py-12 text-gray-400">No safety manuals found</p>
        )}
      </div>
    </AppLayout>
  )
}
